import React, { useCallback, useEffect, useRef, useState } from "react";
import { FlaskConical, Send, RotateCcw, ShieldCheck, AlertTriangle, Wrench, CalendarClock, User, Target, Check, X, CheckCircle2, XCircle, ShieldAlert } from "lucide-react";
import { PageHeader, Button, Input, Textarea, Select, StatusBadge, LoadingBlock, EmptyState, toast } from "@/components/ds";
import { api, formatApiError, type SimSession, type SimScenario, type SimChecklistItem } from "@/lib/api";

function Confidence({ v }: { v: number }) {
  const pct = Math.round((v ?? 0) * 100);
  const tone = v >= 0.75 ? "bg-emerald-500" : v >= 0.5 ? "bg-amber-500" : "bg-red-500";
  return <div className="flex items-center gap-2"><div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100"><div className={`h-full ${tone}`} style={{ width: `${pct}%` }} /></div><span className="text-xs font-semibold text-slate-600">{pct}%</span></div>;
}

function statusTone(s: string) { return s === "passed" || s === "pass" ? "green" : s === "failed" || s === "fail" ? "red" : "slate"; }

function Checklist({ session, onSet }: { session: SimSession; onSet: (k: string, s: string) => void }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="sim-checklist">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-800">Pass / fail checklist</span>
        <StatusBadge tone={statusTone(session.overall_status)} data-testid="sim-overall-status">{session.overall_status}</StatusBadge>
      </div>
      <div className="space-y-1.5">
        {session.checklist.map((c: SimChecklistItem) => (
          <div key={c.key} className="flex items-center justify-between gap-2 rounded-md border border-slate-100 px-2 py-1.5" data-testid={`checklist-${c.key}`}>
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-slate-700">{c.label}</p>
              {!c.manual && <p className="text-[10px] text-slate-400">auto: {c.auto}</p>}
            </div>
            <div className="flex shrink-0 gap-1">
              <button onClick={() => onSet(c.key, c.status === "pass" ? "pending" : "pass")} data-testid={`checklist-${c.key}-pass`}
                className={`rounded p-1 ${c.status === "pass" ? "bg-emerald-100 text-emerald-700" : "text-slate-300 hover:text-emerald-600"}`}><CheckCircle2 className="h-4 w-4" /></button>
              <button onClick={() => onSet(c.key, c.status === "fail" ? "pending" : "fail")} data-testid={`checklist-${c.key}-fail`}
                className={`rounded p-1 ${c.status === "fail" ? "bg-red-100 text-red-700" : "text-slate-300 hover:text-red-600"}`}><XCircle className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Analysis({ session, onDecide }: { session: SimSession; onDecide: (id: string, d: "approve" | "reject") => void }) {
  const last = session.turns[session.turns.length - 1];
  if (!last) return <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">Send a message to see the analysis.</div>;
  return (
    <div className="space-y-3" data-testid="sim-analysis">
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800"><ShieldCheck className="h-4 w-4 text-blue-500" /> Last turn</div>
        <div className="flex items-center justify-between"><span className="text-xs text-slate-400">Intent</span><StatusBadge tone="blue" data-testid="sim-intent">{last.intent}</StatusBadge></div>
        <div className="mt-1"><span className="text-xs text-slate-400">Confidence</span><Confidence v={last.confidence} /></div>
        <div className="mt-1 flex items-center justify-between"><span className="text-xs text-slate-400">Proposed action</span><span className="text-xs font-medium text-slate-700">{last.proposed_action}</span></div>
        {last.escalation_reason && <div className="mt-2 flex items-start gap-1.5 rounded-md bg-red-50 px-2 py-1.5" data-testid="sim-escalation"><ShieldAlert className="mt-0.5 h-3.5 w-3.5 text-red-500" /><p className="text-[11px] text-red-700">Escalated: {last.escalation_reason}</p></div>}
      </div>

      {last.proposed_appointment_times.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="sim-times">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800"><CalendarClock className="h-4 w-4 text-emerald-500" /> Proposed times</div>
          <div className="flex flex-wrap gap-1.5">{last.proposed_appointment_times.slice(0, 8).map((t, i) => <span key={i} className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">{t.local_time}</span>)}</div>
        </div>
      )}

      {last.proposed_tool_calls.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="sim-actions">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800"><Wrench className="h-4 w-4 text-indigo-500" /> Proposed actions (simulated)</div>
          <div className="space-y-2">
            {last.proposed_tool_calls.map((a) => (
              <div key={a.id} className="rounded-md border border-slate-100 bg-slate-50 p-2" data-testid={`sim-action-${a.name}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-indigo-700">{a.name}</span>
                  {a.decision === "pending" ? (
                    <div className="flex gap-1">
                      <button onClick={() => onDecide(a.id, "approve")} data-testid={`approve-${a.id}`} className="rounded bg-emerald-100 p-1 text-emerald-700 hover:bg-emerald-200"><Check className="h-3.5 w-3.5" /></button>
                      <button onClick={() => onDecide(a.id, "reject")} data-testid={`reject-${a.id}`} className="rounded bg-red-100 p-1 text-red-700 hover:bg-red-200"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ) : <StatusBadge tone={a.decision === "approved" ? "green" : "red"}>{a.decision}</StatusBadge>}
                </div>
                <p className="mt-0.5 truncate text-[10px] text-slate-500">{JSON.stringify(a.arguments)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {(last.contact_changes.length > 0 || last.lead_changes.length > 0) && (
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="sim-crm-changes">
          <div className="mb-2 text-sm font-semibold text-slate-800">Proposed CRM changes</div>
          {last.contact_changes.map((c, i) => <div key={i} className="flex items-center gap-1.5 text-[11px] text-slate-600"><User className="h-3 w-3 text-slate-400" /> Contact: {JSON.stringify((c as any).data ?? c)}</div>)}
          {last.lead_changes.map((l, i) => <div key={i} className="flex items-center gap-1.5 text-[11px] text-slate-600"><Target className="h-3 w-3 text-slate-400" /> Lead: {JSON.stringify(l)}</div>)}
        </div>
      )}

      {last.knowledge_sources.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="sim-sources">
          <div className="mb-2 text-sm font-semibold text-slate-800">Knowledge sources used</div>
          {last.knowledge_sources.map((s, i) => <div key={i} className="mb-1 rounded bg-blue-50/50 px-2 py-1"><p className="text-xs font-medium text-slate-700">{s.title}</p><p className="truncate text-[11px] text-slate-500">{s.snippet}</p></div>)}
        </div>
      )}
    </div>
  );
}

export default function Simulator() {
  const [scenarios, setScenarios] = useState<SimScenario[]>([]);
  const [configs, setConfigs] = useState<{ id: string; name: string }[]>([]);
  const [model, setModel] = useState<{ model: string; configured: boolean } | null>(null);
  const [configId, setConfigId] = useState("default");
  const [scenarioKey, setScenarioKey] = useState("");
  const [session, setSession] = useState<SimSession | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.simMeta().then((m) => {
      setScenarios(m.scenarios); setConfigs(m.configs); setModel({ model: m.model, configured: m.configured });
      if (m.scenarios[0]) setScenarioKey(m.scenarios[0].key);
      if (m.configs[0]) setConfigId(m.configs[0].id);
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [session?.turns.length]);

  const start = async () => {
    if (!scenarioKey) return;
    setBusy(true);
    try {
      const s = await api.simCreate(scenarioKey, configId);
      setSession(s);
      // Auto-send the scenario's opening line as the first customer message.
      const first = await api.simMessage(s.id, s.opening);
      setSession(first);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const send = async () => {
    if (!session || !text.trim() || busy) return;
    const body = text.trim(); setText(""); setBusy(true);
    try { setSession(await api.simMessage(session.id, body)); }
    catch (e) { toast.error(formatApiError(e)); setText(body); } finally { setBusy(false); }
  };
  const decide = async (id: string, d: "approve" | "reject") => {
    if (!session) return;
    try { setSession(await api.simDecide(session.id, id, d)); } catch (e) { toast.error(formatApiError(e)); }
  };
  const setCheck = async (k: string, s: string) => {
    if (!session) return;
    try { setSession(await api.simChecklist(session.id, k, s)); } catch (e) { toast.error(formatApiError(e)); }
  };
  const reset = async () => {
    if (!session) { setSession(null); return; }
    try { setSession(await api.simReset(session.id)); toast.success("Reset"); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="page-simulator" className="space-y-4">
      <PageHeader title="AI Receptionist Simulator" description="Rehearse scenarios in a fully isolated sandbox — no real calls, SMS, calendar events or customers." testid="simulator-header"
        action={<div className="flex items-center gap-2">{model && <StatusBadge tone={model.configured ? "green" : "red"}><FlaskConical className="h-3.5 w-3.5" /> {model.configured ? model.model : "Not configured"}</StatusBadge>}</div>} />

      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-white p-3" data-testid="sim-controls">
        <div className="w-56"><label className="mb-1 block text-xs text-slate-400">Configuration</label><Select value={configId} onChange={setConfigId} options={configs.map((c) => ({ value: c.id, label: c.name }))} testid="config-select" /></div>
        <div className="w-64"><label className="mb-1 block text-xs text-slate-400">Scenario</label><Select value={scenarioKey} onChange={setScenarioKey} options={scenarios.map((s) => ({ value: s.key, label: s.title }))} testid="scenario-select" /></div>
        <Button onClick={start} disabled={busy || !scenarioKey || !model?.configured} data-testid="start-scenario-btn"><FlaskConical className="h-4 w-4" /> Start scenario</Button>
        {session && <Button variant="outline" onClick={reset} data-testid="reset-btn"><RotateCcw className="h-4 w-4" /> Reset</Button>}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_340px]" style={{ minHeight: "56vh" }}>
        <div className="flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="sim-thread">
          {!session ? (
            <div className="flex flex-1 items-center justify-center p-8"><EmptyState icon={FlaskConical} title="Pick a scenario and start" description="The opening customer message is sent automatically; then continue the conversation." /></div>
          ) : (
            <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="sim-scroll">
              {session.turns.map((t) => (
                <div key={t.id} className="space-y-2">
                  <div className="flex justify-end"><div className="max-w-[75%] rounded-2xl rounded-tr-sm border border-slate-200 bg-slate-50 px-4 py-2.5" data-testid="sim-customer-msg"><p className="text-[11px] font-semibold text-slate-500">Customer</p><p className="text-sm text-slate-800">{t.customer_text}</p></div></div>
                  <div className="flex justify-start"><div className="max-w-[75%] rounded-2xl rounded-tl-sm border border-blue-200 bg-blue-50 px-4 py-2.5" data-testid="sim-ai-msg"><p className="text-[11px] font-semibold text-blue-600">AI receptionist</p><p className="whitespace-pre-wrap text-sm text-slate-800">{t.ai_response}</p></div></div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
          )}
          <div className="border-t border-slate-100 p-3">
            <div className="flex items-end gap-2">
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} disabled={!session} data-testid="sim-input"
                placeholder={session ? "Continue as the customer…" : "Start a scenario first"}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
              <Button onClick={send} disabled={busy || !session || !text.trim()} data-testid="sim-send"><Send className="h-4 w-4" /> {busy ? "…" : "Send"}</Button>
            </div>
          </div>
        </div>

        <div className="space-y-4 overflow-y-auto">
          {session && <Checklist session={session} onSet={setCheck} />}
          {session && <Analysis session={session} onDecide={decide} />}
        </div>
      </div>
    </div>
  );
}
